import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge, Card, EmptyState, ErrorAlert, Pagination, Spinner } from './ui';

type SocialPost = {
  item: { id: string; topic: string; status: string; contentType?: string; updatedAt: string };
  variant: { id: string; platform: string; kind?: string; body: string; cta: string | null };
  publishStatus: string;
  publishDetail: string | null;
  publishTone: 'success' | 'danger' | 'warning' | 'info' | 'neutral';
  scheduledAt: string | null;
  result?: { id: string; externalId: string | null };
};

type SocialFilter = 'ALL' | 'POST' | 'REEL';

const platformNames: Record<string, string> = { FACEBOOK: 'Facebook', INSTAGRAM: 'Instagram', TIKTOK: 'TikTok', YOUTUBE: 'YouTube', X: 'X', LINKEDIN: 'LinkedIn', TELEGRAM: 'Telegram' };

function PostUrl({ result }: { result?: { id: string; externalId: string | null } }) {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!result?.externalId) return null;
  const resolve = async () => {
    setBusy(true);
    try {
      const response = await fetch(`/api/publish/${result.id}/url`, { method: 'POST' });
      const data = await response.json() as { postUrl?: string | null };
      if (data.postUrl) setUrl(data.postUrl);
    } finally { setBusy(false); }
  };
  return url
    ? <a href={url} target="_blank" rel="noreferrer" className="btn btn--sm">Otwórz post</a>
    : <button className="btn btn--sm" onClick={resolve} disabled={busy}>{busy ? '...' : 'URL'}</button>;
}

function deliveryState(itemStatus: string, result: { status: string; message: string | null; payload: Record<string, unknown> } | undefined, scheduledAt: string | null) {
  if (result?.status === 'SUCCESS') {
    const mode = result.payload?.mode;
    if (mode === 'schedule') return { label: 'Wysłane do Sociamonials — zaplanowane', detail: result.message, tone: 'success' as const };
    return { label: 'Wysłane do Sociamonials', detail: result.message, tone: 'success' as const };
  }
  if (result?.status === 'FAILED') return { label: 'Błąd wysyłki', detail: result.message, tone: 'danger' as const };
  if (result?.status === 'PARTIAL') return { label: 'Wysłane częściowo', detail: result.message, tone: 'warning' as const };
  if (scheduledAt) return { label: 'Zaplanowane w Kontentowo', detail: 'Nie wysłano jeszcze do Sociamonials', tone: 'info' as const };
  if (itemStatus === 'APPROVED') return { label: 'Zatwierdzone — nie wysłane', detail: 'Czeka na zaplanowanie lub ręczną publikację', tone: 'warning' as const };
  return { label: 'Przygotowane', detail: 'Nie wysłano do Sociamonials', tone: 'neutral' as const };
}

export function SocialPostsPanel({ brandId, title = 'Social media — ostatnie posty', limit = 200 }: { brandId?: string | null; title?: string; limit?: number }) {
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [filter, setFilter] = useState<SocialFilter>('ALL');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [page, setPage] = useState(1);
  const pageSize = 8;

  useEffect(() => {
    if (!brandId) { setPosts([]); return; }
    let alive = true;
    setLoading(true); setError(null); setPage(1);
    api.content.list({ brandId })
      .then(async (items) => {
        const social = items.filter((item) => item.contentType === 'post').slice(0, limit);
        const details = await Promise.all(social.map((item) => api.content.get(item.id)));
        const rows: SocialPost[] = [];
        for (const detail of details) {
          for (const variant of detail.variants) {
            const result = detail.results.filter((entry) => entry.variantId === variant.id).sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
            const schedule = detail.schedules.find((entry) => entry.variantId === variant.id && entry.status === 'PENDING');
            const state = deliveryState(detail.item.status, result, schedule?.scheduledAt ?? null);
            rows.push({ item: detail.item, variant, publishStatus: state.label, publishDetail: state.detail, publishTone: state.tone, scheduledAt: schedule?.scheduledAt ?? null, result: result ? { id: result.id, externalId: result.externalId } : undefined });
          }
        }
        if (alive) setPosts(rows);
      })
      .catch((reason) => { if (alive) setError(reason); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [brandId, limit]);

  const filtered = posts.filter((row) => filter === 'ALL' || (filter === 'REEL' ? row.variant.kind === 'VIDEO' || row.item.contentType === 'video' : row.variant.kind !== 'VIDEO' && row.item.contentType !== 'video'));
  const visible = filtered.slice((page - 1) * pageSize, page * pageSize);
  return <Card title={title} actions={<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><select value={filter} onChange={(e) => { setFilter(e.target.value as SocialFilter); setPage(1); }} aria-label="Filtr typu treści"><option value="ALL">Wszystkie</option><option value="POST">Posty</option><option value="REEL">Rolki</option></select><span className="muted">{filtered.length} wariantów</span></div>}>
    {loading && <Spinner label="Ładuję posty social…" />}
    <ErrorAlert error={error} />
    {!loading && !filtered.length && <EmptyState icon="◎" title="Brak treści dla tego filtra" hint="Zmień filtr albo wygeneruj pierwszy post lub rolkę." />}
    {visible.length > 0 && <table className="table"><thead><tr><th>Platforma</th><th>Temat / treść</th><th>Status wysyłki</th><th>Termin</th><th>Link</th><th /></tr></thead><tbody>{visible.map((row) => <tr key={row.variant.id}><td><Badge>{platformNames[row.variant.platform] ?? row.variant.platform}</Badge></td><td><strong>{row.item.topic}</strong><div className="muted" style={{ maxWidth: 560, whiteSpace: 'pre-wrap' }}>{row.variant.body.slice(0, 260)}{row.variant.body.length > 260 ? '…' : ''}</div>{row.variant.cta && <div className="muted" style={{ fontSize: 11 }}>CTA: {row.variant.cta}</div>}</td><td><Badge tone={row.publishTone}>{row.publishStatus}</Badge>{row.publishDetail && <div className="muted" style={{ fontSize: 11, marginTop: 4, maxWidth: 260 }}>{row.publishDetail}</div>}</td><td className="muted">{row.scheduledAt ? new Date(row.scheduledAt).toLocaleString('pl-PL') : '—'}</td><td><PostUrl result={row.result} /></td><td><Link className="btn btn--sm" to={`/content/${row.item.id}`}>Podgląd</Link></td></tr>)}</tbody></table>}
    <Pagination page={page} pageSize={pageSize} total={filtered.length} onChange={setPage} />
  </Card>;
}
